import { useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BookOpen, Compass, LogOut, RotateCcw } from "lucide-react";
import { useAuthStore } from "@/stores/auth";
import { useDemoWorkspaceStore } from "@/stores/demoWorkspace";
import { useProductTour } from "@/hooks/useProductTour";
import { chaptersForUser, nextChapter, tourCounts } from "@/lib/productTour";
import { getLiveChapterRoute, getLiveChapterSteps } from "@/lib/liveProductTour";
import { demoTourContext, startLiveChapter } from "@/lib/liveTourSession";
import { exitDemoWorkspace, resetDemoWorkspace } from "@/lib/demoSession";
import { LiveTourSpotlight } from "./LiveTourSpotlight";
import "./liveTour.css";
import { useIsMobileViewport } from "@/hooks/useMediaQuery";

/** The shared shell hosts the same real-page tour on desktop, mobile and public views. */
export function TourCoach() {
  const user = useAuthStore((s) => s.user);
  const demoActive = useDemoWorkspaceStore((s) => s.active);
  const demoOwner = useDemoWorkspaceStore((s) => s.ownerId);
  const location = useLocation();
  const navigate = useNavigate();
  const { state, ready, syncError, update } = useProductTour();
  const chapters = chaptersForUser(!!user?.is_superadmin);
  const current = chapters.find((item) => item.id === state.current_chapter);
  const isMobile = useIsMobileViewport();
  const context = useMemo(() => ({ ...demoTourContext(), isMobile }), [isMobile]);
  const steps = useMemo(() => current ? getLiveChapterSteps(current.id, context) : [], [current, context]);
  const index = Math.min(state.current_step, Math.max(0, steps.length - 1));
  const step = steps[index];
  const pause = useCallback(() => {
    update({ status: "paused" });
    exitDemoWorkspace();
    navigate("/learn");
  }, [update, navigate]);
  if (!user || !ready) return null;
  const active = demoActive && demoOwner === user.id;
  const counts = tourCounts(chapters, state);

  function advanceFeature(skipped: boolean) {
    if (!current) return;
    update({ chapter_id: current.id, chapter_status: skipped ? "skipped" : "completed" });
    const updated = { ...state, chapters: { ...state.chapters, [current.id]: skipped ? "skipped" as const : "completed" as const } };
    const next = nextChapter(chapters, updated);
    if (next) navigate(startLiveChapter(next.id, user!.id));
    else {
      update({ status: chapters.every((chapter) => updated.chapters[chapter.id] === "completed") ? "completed" : "paused", current_chapter: null });
      exitDemoWorkspace();
      navigate("/learn");
    }
  }
  function move(nextIndex: number) {
    if (!current) return;
    update({ current_chapter: current.id, current_step: nextIndex });
    const route = steps[nextIndex]?.route;
    // Keep native selections (day, stage, drawer) while steps share a page.
    if (route && route !== steps[index]?.route) navigate(route);
  }
  return <>
    {active && <section className="demo-workspace-banner" aria-label="Demo workspace">
      <Compass size={17} aria-hidden="true" /><div><strong>Trace demo workspace</strong><span>Real tools · fictional festival · changes stay in this demo</span></div>
      <button onClick={() => { resetDemoWorkspace(); if (current) { update({ current_chapter: current.id, current_step: 0 }); navigate(getLiveChapterRoute(current.id, context) ?? "/learn"); } }}><RotateCcw size={12} />Reset demo</button>
      <button onClick={pause}><LogOut size={12} />Exit demo</button>
    </section>}
    {!active && !location.pathname.startsWith("/learn") && (state.status === "not_started" || state.status === "in_progress") && <section className="tour-invitation" aria-label="Explore Trace on real pages">
      <Compass size={20} aria-hidden="true" /><div><strong>Discover everything Trace can do</strong><span>{counts.total} features, real screens and a complete interactive demo festival.</span></div>
      <Link className="btn btn--accent btn--sm" to={state.current_chapter ? `/learn/${state.current_chapter}` : "/learn"}>{state.status === "in_progress" ? "Resume tour" : "Explore Trace"}<BookOpen size={14} /></Link>
      <button className="tour-text-action" onClick={() => update({ status: "skipped" })}>Skip tour</button>
    </section>}
    {active && state.status === "in_progress" && current && step && !location.pathname.startsWith("/learn") && <LiveTourSpotlight
      key={`${current.id}:${index}`} chapterId={current.id} chapterTitle={current.title} step={step} index={index} total={steps.length} ownerId={user.id} syncError={syncError}
      onNext={() => index < steps.length - 1 ? move(index + 1) : advanceFeature(false)} onBack={() => move(Math.max(0, index - 1))}
      onPause={pause} onSkip={() => advanceFeature(true)}
      onReturn={() => navigate(step.route ?? getLiveChapterRoute(current.id, context) ?? "/learn")}
      onRestart={() => { move(0); navigate(getLiveChapterRoute(current.id, context) ?? "/learn"); }}
    />}
  </>;
}
